<template>
	<el-row type="flex" align="middle" class="third-measure-control">
		<template v-if="config.controlType === 'select'">
			<el-col :span="selectedOption && selectedOption.controlType !== 'none' ? 6 : 24">
				<el-select :value="result.value" :disabled="disabled" clearable placeholder="请选择" size="small"
					:aria-label="config.name" @change="changeOption">
					<!-- option 为当前测量项的候选配置。 -->
					<el-option v-for="option in config.options || []" :key="option.key" :label="option.name"
						:value="String(option.key)" />
				</el-select>
			</el-col>
			<el-col v-if="selectedOption && selectedOption.controlType !== 'none'" :span="18" class="measure-child">
				<ThirdMeasureControl :key="selectedOption.key" :config="selectedOption" :value="result.options[0]"
					:disabled="disabled" @input="changeChild" />
			</el-col>
		</template>
		<template v-else-if="inputCount">
			<!-- position 为数字框的一基索引；数值数组保留空位置。 -->
			<template v-for="position in inputCount">
				<span v-if="position > 1 && separator" :key="'separator-' + position" class="measure-separator">
					{{ separator }}
				</span>
				<el-input :key="position" :value="inputValues[position - 1] || ''" type="number" step="0.01" size="small"
					:placeholder="placeholder" :disabled="disabled" :aria-label="config.name + ' ' + position"
					@input="changeNumber(position - 1, $event)">
					<template v-if="inputPrefix(position)" slot="prefix">{{ inputPrefix(position) }}</template>
				</el-input>
			</template>
		</template>
	</el-row>
</template>

<script>
// 复用测量结果的构造与数值拆分，确保编辑和提交使用同一格式。
import { createMeasureResult, getMeasureInputCount, getMeasureInputPlaceholder, getMeasureInputPrefix, getMeasureSeparator, limitMeasureDecimal, splitMeasureValue } from "./thirdMeasure";

export default {
	name: "ThirdMeasureControl",
	props: {
		// 当前层级的测量控件配置。
		config: { type: Object, required: true },
		// 父级 v-model 传入的测量结果。
		value: { type: Object, default: () => ({}) },
		// 跟随批次包测量权限禁用所有层级的输入。
		disabled: { type: Boolean, default: false },
	},
	computed: {
		// 合并默认值与回显记录，避免直接修改传入的对象。
		result() {
			return createMeasureResult(this.config, this.value);
		},
		// 选中的子配置决定下拉框右侧显示哪种控件。
		selectedOption() {
			return (this.config.options || []).find((option) => String(option.key) === this.result.value);
		},
		// 当前数字控件需要展示的输入框数量。
		inputCount() {
			return getMeasureInputCount(this.config.controlType);
		},
		// 宽高 range 用 x，有调节器 range 不显示分隔符，其余 range 仍用 ~，多值用 /。
		separator() {
			return getMeasureSeparator(this.config);
		},
		// number、range、multiNumber 的输入框展示后端单位。
		placeholder() {
			return getMeasureInputPlaceholder(this.config);
		},
		// 回显每一个数字框，保留范围和多值测量的顺序。
		inputValues() {
			return splitMeasureValue(this.result.value);
		},
	},
	methods: {
		// value 为新选项编码；切换分支时清除上一个选项的隐藏数值。
		inputPrefix(position) {
			return getMeasureInputPrefix(this.config, position);
		},
		changeOption(value) {
			this.$emit("input", createMeasureResult(this.config, { value }));
		},
		// value 为子控件最新结果，仅保留当前选中的分支。
		changeChild(value) {
			this.$emit("input", { ...this.result, options: [value] });
		},
		// index 为编辑位置，value 为输入文本；多个数字仍以字符串提交。
		changeNumber(index, value) {
			const formatted = limitMeasureDecimal(value);
			// 按控件数量补齐空值，防止前一个输入框为空时后一个数值错位。
			const values = Array.from({ length: this.inputCount }, (_, position) => this.inputValues[position] || "");
			values[index] = formatted;
			this.$emit("input", {
				...this.result,
				value: values.every((item) => item === "") ? "" : values.join(","),
			});
			// 截断后若父级值未变，原生 number 框仍会留下多出来的小数位。
			this.$nextTick(() => {
				const inputs = this.$el && this.$el.querySelectorAll("input.el-input__inner[type='number']");
				if (inputs && inputs[index] && inputs[index].value !== formatted) {
					inputs[index].value = formatted;
				}
			});
		},
	},
};
</script>

<style scoped lang="less">
.third-measure-control {
	width: 100%;
	min-width: 0;
	text-align: left;

	.el-select,
	.el-input {
		min-width: 0;
		width: 100%;
	}

	>.el-input {
		flex: 1;
	}

	::v-deep .el-input__prefix {
		display: flex;
		align-items: center;
		left: 8px;
		color: #303133;
		font-size: 12px;
		line-height: 1;
	}

	::v-deep .el-input--prefix .el-input__inner {
		padding-left: 72px;
	}
}

.measure-child {
	padding-left: 12px;
}

.measure-separator {
	flex-shrink: 0;
	padding: 0 4px;
	color: #909399;
}
</style>
